import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

// Federal CDL restriction codes (49 CFR 383.95 / AAMVA) — [code, short label, what it means]
export const CDL_RESTRICTIONS = [
  ["E", "No manual", "May only drive automatic-transmission CMVs"],
  ["L", "No air brakes", "May not drive a CMV with full air brakes"],
  ["Z", "No full air brakes", "Tested in a vehicle with air-over-hydraulic brakes"],
  ["M", "No Class A bus", "May not drive a Class A passenger vehicle"],
  ["N", "No Class A/B bus", "May not drive a Class A or B passenger vehicle"],
  ["O", "No tractor-trailer", "May not drive a tractor-trailer combination"],
  ["P", "No passengers", "CLP holder — no passengers in a bus"],
  ["X", "No cargo in tank", "CLP holder — tank vehicle must be empty"],
  ["V", "Medical variance", "Drives under an FMCSA medical variance / exemption"],
];
export const RESTRICTION_LABEL = { ...Object.fromEntries(CDL_RESTRICTIONS.map(([c, s]) => [c, s])), K: "Intrastate only" };
const LABEL = RESTRICTION_LABEL;

export const CDL_CLASSES = [["A", "Combination vehicles 26,001+ lbs GCWR, towing 10,001+ lbs"], ["B", "Single vehicle 26,001+ lbs GVWR (may tow ≤10,000 lbs)"], ["C", "Small vehicles carrying 16+ passengers or placarded hazmat"]];
export const ENDORSEMENTS = [["H", "Hazmat", "Placarded hazardous materials"], ["N", "Tank", "Tank vehicles (liquids / gases in bulk)"], ["P", "Passenger", "Vehicles designed for 16+ passengers"], ["S", "School bus", "School buses (requires P)"], ["T", "Doubles/Triples", "Double or triple trailers"], ["X", "Tank + Hazmat", "Combination of N and H"]];
export const ENDORSEMENT_LABEL = Object.fromEntries(ENDORSEMENTS.map(([c, s]) => [c, s]));

export function ClassEndorsementFields({ form, setForm }) {
  const list = form.endorsements || [];
  const toggle = (code, v) => {
    const next = v ? [...list, code] : list.filter((c) => c !== code);
    const hz = next.includes("X") ? "X" : next.includes("H") ? "H" : (form.hazmat_endorsement === "H" || form.hazmat_endorsement === "X" ? "none" : form.hazmat_endorsement);
    setForm({ ...form, endorsements: next, hazmat_endorsement: hz });
  };
  return (
    <div className="col-span-2 grid sm:grid-cols-[160px_1fr] gap-3 rounded-md border border-slate-200 p-3" data-testid="driver-class-endorsements-panel">
      <div>
        <Label className="text-sm">CDL class</Label>
        <div className="mt-1 inline-flex rounded-md border border-slate-300 overflow-hidden">
          {CDL_CLASSES.map(([c, d]) => <button type="button" key={c} title={d} onClick={() => setForm({ ...form, cdl_class: form.cdl_class === c ? null : c })} className={`px-3 py-1.5 text-sm font-mono font-semibold border-r last:border-r-0 border-slate-300 ${form.cdl_class === c ? "bg-blue-700 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid={`driver-cdl-class-${c}`}>{c}</button>)}
        </div>
        <div className="text-[11px] text-slate-500 mt-1 leading-snug">{form.cdl_class ? CDL_CLASSES.find(([c]) => c === form.cdl_class)[1] : "Tap a class — read from the MVR when available"}</div>
      </div>
      <div>
        <Label className="text-sm">Endorsements</Label>
        <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {ENDORSEMENTS.map(([code, short, desc]) => (
            <label key={code} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs cursor-pointer transition-colors ${list.includes(code) ? "border-blue-400 bg-blue-50" : "border-transparent hover:bg-slate-50"}`} data-testid={`driver-endorsement-${code}`}>
              <Checkbox checked={list.includes(code)} onCheckedChange={(v) => toggle(code, !!v)} className="mt-0.5" />
              <span className="min-w-0"><span className="inline-flex items-center gap-1.5"><b className="font-mono rounded bg-blue-100 text-blue-900 px-1">{code}</b><span className="font-medium">{short}</span></span><div className="text-[11px] text-slate-500 leading-snug">{desc}</div></span>
            </label>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">Ticking H or X also sets the hazmat endorsement below so TSA / training tracking kicks in.</div>
      </div>
    </div>
  );
}

export function RestrictionsFields({ form, setForm }) {
  const list = form.cdl_restrictions || [];
  const [on, setOn] = useState(list.length > 0 || !!form.intrastate_only);
  useEffect(() => { if (list.length > 0 || form.intrastate_only) setOn(true); }, [list.length, form.intrastate_only]);
  const toggle = (code, v) => setForm({ ...form, cdl_restrictions: v ? [...list, code] : list.filter((c) => c !== code) });
  const flip = (v) => { setOn(v); if (!v) setForm({ ...form, cdl_restrictions: [], intrastate_only: false }); };
  return (
    <div className="col-span-2 rounded-md border border-sky-200 bg-sky-50/50 p-3 space-y-3" data-testid="driver-restrictions-panel">
      <div className="flex items-center justify-between">
        <div><Label className="text-sm">Restrictions listed on MVR</Label><div className="text-xs text-slate-500">Turn on to record the restriction codes printed on the driver's CDL / MVR.</div></div>
        <Switch checked={on} onCheckedChange={flip} data-testid="driver-restrictions-toggle" />
      </div>
      {on && (
        <>
          <div className="grid sm:grid-cols-3 gap-2">
            {CDL_RESTRICTIONS.map(([code, short, desc]) => (
              <label key={code} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs cursor-pointer transition-colors ${list.includes(code) ? "border-sky-400 bg-white" : "border-transparent hover:bg-white/70"}`} data-testid={`driver-restriction-${code}`}>
                <Checkbox checked={list.includes(code)} onCheckedChange={(v) => toggle(code, !!v)} className="mt-0.5" />
                <span className="min-w-0"><span className="inline-flex items-center gap-1.5"><b className="font-mono rounded bg-sky-100 text-sky-900 px-1">{code}</b><span className="font-medium">{short}</span></span><div className="text-[11px] text-slate-500 leading-snug">{desc}</div></span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <div><Label className="text-sm">Intrastate only (K)</Label><div className="text-xs text-slate-600">Driver may not operate across state lines — excepted/non-excepted intrastate certification.</div></div>
            <Switch checked={!!form.intrastate_only} onCheckedChange={(v) => setForm({ ...form, intrastate_only: v })} data-testid="driver-intrastate-toggle" />
          </div>
        </>
      )}
    </div>
  );
}

export function restrictionSummary(d) {
  const codes = [...(d.cdl_restrictions || [])];
  if (d.intrastate_only) codes.push("K");
  return codes;
}

export function RestrictionBadges({ driver, testid }) {
  const codes = restrictionSummary(driver);
  if (!codes.length) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-1" data-testid={testid}>
      {codes.map((c) => <Badge key={c} className={`font-mono border ${c === "K" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-sky-50 text-sky-800 border-sky-200"}`} title={LABEL[c]}>{c}</Badge>)}
    </span>
  );
}

export function restrictionText(d) {
  const codes = restrictionSummary(d);
  return codes.length ? codes.map((c) => `${c} — ${LABEL[c]}`).join("; ") : "None";
}
